import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  Chip,
  Input,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  Select,
  Option
} from '@mui/joy';
import {
  Star as StarIcon,
  MagnifyingGlass as SearchIcon,
  X as CloseIcon,
  User as PersonIcon,
  Calendar as DateIcon,
  CheckCircle as SelectIcon
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { format } from 'date-fns';
import type { Review } from '../../types/marketing';

// Re-export for backwards compatibility
export type { Review };

interface TestimonialSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (review: Review) => void;
  selectedReviewId?: number;
}

export const TestimonialSelector: React.FC<TestimonialSelectorProps> = ({
  open,
  onClose,
  onSelect,
  selectedReviewId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState<number | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'rating'>('date');

  // Fetch reviews from GBP
  const { data: reviews, isLoading } = useQuery({
    queryKey: ['reviews-for-testimonials'],
    queryFn: async () => {
      const response = await api.get('/api/marketing/reviews/?min_rating=4');
      return (response.data as any)?.results || [];
    }
  });

  // Filter and sort reviews
  const filteredReviews = React.useMemo(() => {
    if (!reviews) return [];

    let filtered = [...reviews];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((review: Review) =>
        review.reviewer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.review_text.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply rating filter
    if (ratingFilter !== 'all') {
      filtered = filtered.filter((review: Review) => review.rating === ratingFilter);
    }

    // Apply source filter (using platform_name)
    if (sourceFilter !== 'all') {
      filtered = filtered.filter((review: Review) => review.platform_name === sourceFilter);
    }

    // Sort reviews
    filtered.sort((a: Review, b: Review) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'date':
        default:
          return new Date(b.review_date).getTime() - new Date(a.review_date).getTime();
      }
    });

    return filtered;
  }, [reviews, searchQuery, ratingFilter, sourceFilter, sortBy]);

  // Get unique platforms
  const platforms = React.useMemo(() => {
    if (!reviews) return [];
    const uniquePlatforms = new Set(reviews.map((r: Review) => r.platform_name));
    return Array.from(uniquePlatforms);
  }, [reviews]);

  const handleSelectReview = (review: Review) => {
    onSelect(review);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 900, maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        <DialogTitle>Select Customer Testimonial</DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Stack spacing={3}>
            {/* Filters */}
            <Box>
              <Stack spacing={2}>
                {/* Search */}
                <FormControl>
                  <Input
                    placeholder="Search reviews..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startDecorator={<SearchIcon />}
                    endDecorator={
                      searchQuery && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => setSearchQuery('')}
                        >
                          <CloseIcon />
                        </IconButton>
                      )
                    }
                  />
                </FormControl>

                {/* Filter Controls */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Rating Filter */}
                  <FormControl size="sm">
                    <FormLabel>Rating</FormLabel>
                    <Select
                      value={ratingFilter as any}
                      onChange={(_, value) => {
                        if (value === 'all') {
                          setRatingFilter('all');
                        } else {
                          setRatingFilter(value as number);
                        }
                      }}
                    >
                      <Option value="all">All Ratings</Option>
                      <Option value={5}>5 Stars</Option>
                      <Option value={4}>4 Stars</Option>
                      <Option value={3}>3 Stars</Option>
                    </Select>
                  </FormControl>

                  {/* Source Filter */}
                  <FormControl size="sm">
                    <FormLabel>Source</FormLabel>
                    <Select
                      value={sourceFilter}
                      onChange={(_, value) => setSourceFilter(value as string)}
                    >
                      <Option value="all">All Sources</Option>
                      {platforms.map((platform: any) => (
                        <Option key={platform} value={platform}>
                          {platform}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Sort By */}
                  <FormControl size="sm">
                    <FormLabel>Sort By</FormLabel>
                    <Select
                      value={sortBy}
                      onChange={(_, value) => setSortBy(value as 'date' | 'rating')}
                    >
                      <Option value="date">Newest First</Option>
                      <Option value="rating">Highest Rating</Option>
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Box>

            {/* Results */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredReviews.length === 0 ? (
              <Alert color="neutral">
                {searchQuery || ratingFilter !== 'all' || sourceFilter !== 'all'
                  ? 'No reviews found matching your criteria.'
                  : 'No customer reviews available yet.'}
              </Alert>
            ) : (
              <Stack spacing={2}>
                {filteredReviews.map((review: Review) => (
                  <Card
                    key={review.id}
                    variant={selectedReviewId === review.id ? 'solid' : 'outlined'}
                    color={selectedReviewId === review.id ? 'primary' : 'neutral'}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 'md',
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => handleSelectReview(review)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          {/* Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon size={20} />
                              <Typography level="title-md">
                                {review.reviewer_name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {[...Array(5)].map((_, i) => (
                                <StarIcon
                                  key={i}
                                  size={16}
                                  color={i < review.rating ? 'var(--joy-palette-warning-500)' : 'var(--joy-palette-neutral-300)'}
                                />
                              ))}
                            </Box>
                            <Chip size="sm" variant="soft">
                              {review.platform_name}
                            </Chip>
                          </Box>

                          {/* Review Text */}
                          <Typography level="body-md" sx={{ mb: 1 }}>
                            "{review.review_text}"
                          </Typography>

                          {/* Date */}
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            <DateIcon size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            {format(new Date(review.review_date), 'MMMM d, yyyy')}
                          </Typography>

                          {/* Response if exists */}
                          {review.response_text && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                              <Typography level="body-sm" fontWeight="lg" sx={{ mb: 0.5 }}>
                                Your Response:
                              </Typography>
                              <Typography level="body-sm">
                                {review.response_text}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Select Indicator */}
                        {selectedReviewId === review.id && (
                          <SelectIcon color="var(--joy-palette-primary-500)" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="body-sm">
                {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''} found
              </Typography>
              <Button variant="plain" onClick={onClose}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};